import React, { useState, useEffect } from 'react';
import {
  Card, CircularProgress, Box, Chip, Typography,
  FormControl, InputLabel, Select, MenuItem, Divider,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, AccountTreeOutlined } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { useServicePlanApi } from '../services/servicePlanApi';
import { Axios } from 'services/axios';
import { useDuration } from 'context/DurationContext';
import { useCountry } from 'context/CountryContext';
import { Notify } from 'notiflix';
import MappingForm from './MappingForm';
import ViewMappingModal from './ViewMappingModal';

import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

// import MappingJsonTools from './MappingJsonTools';
import { categoriesApi } from 'api/categoriesApi';
import { featuresApi } from 'api/featuresApi';
import { readTierConfig, tierPriceCells, orderByFlow, activeQuestions } from './mappingUtils';

// ── Design tokens (matches FeaturesPage / QuestionsPage)
const ACCENT  = '#667eea';
const ACCENT2 = '#764ba2';

const TIER_COLOR = { low: '#34d399', mid: '#fbbf24', high: '#a78bfa' };
const TIER_DIM   = { low: 'rgba(52,211,153,0.1)', mid: 'rgba(251,191,36,0.1)', high: 'rgba(167,139,250,0.1)' };
const TIER_LABEL = { low: 'Low', mid: 'Mid', high: 'High' };

// ── Duration label for a duration object (from DurationContext)
const formatDurationLabel = (duration) => {
  if (!duration) return '';
  if (duration.displayLabel) return duration.displayLabel;
  if (duration.label) return duration.label;
  const value = duration.value || 0;
  const unit = duration.unit || 'month';
  const unitLabels = { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', biannual: '6 Months', year: 'Year', custom: 'Custom' };
  return `${value} ${unitLabels[unit] || unit}${value > 1 ? 's' : ''}`;
};

// ── Tier badge (matches reference style)
function TierBadge({ tier }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: TIER_COLOR[tier], background: TIER_DIM[tier], padding: '3px 10px', borderRadius: 4,
    }}>
      {TIER_LABEL[tier] || tier}
    </span>
  );
}

// ── Action button (small icon-style)
function ActionBtn({ icon, title, color, onClick }) {
  return (
    <Box
      component="button"
      title={title}
      onClick={onClick}
      sx={{
        width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid transparent', borderRadius: 1.5, background: 'transparent',
        cursor: 'pointer', transition: 'all 0.15s', p: 0,
        '&:hover': { background: `${color}18`, borderColor: `${color}44`, color },
        color: 'text.secondary',
      }}
    >
      {icon}
    </Box>
  );
}

function MappingsPage() {
  const canRead = usePermission('qa-center', 'read');
  const canCreate = usePermission('qa-center', 'create');
  const { mappingsApi, questionsApi, answerOptionsApi, questionnairesApi, qaFeaturesApi } = useServicePlanApi();
  const { durations } = useDuration();
  const { countries } = useCountry();

  const [mappings,               setMappings]               = useState([]);
  const [questions,              setQuestions]              = useState([]);
  const [features,               setFeatures]               = useState([]);
  const [addOns,                 setAddOns]                 = useState([]);
  const [questionnaires,         setQuestionnaires]         = useState([]);
  const [selectedQuestionnaireId,setSelectedQuestionnaireId]= useState('');
  const [selectedCountryId,      setSelectedCountryId]      = useState('');
  const [loading,                setLoading]                = useState(false);
  const [view,                   setView]                   = useState('list'); // 'list' | 'create' | 'edit'
  const [viewModalOpen,          setViewModalOpen]          = useState(false);
  const [selectedMapping,        setSelectedMapping]        = useState(null);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [canRead]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchMappings(), fetchQuestions(), fetchQuestionnaires(), fetchFeatures(), fetchAddOns()]);
    } catch {
      Notify.failure('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Mapping tiers store shop features (global_features) — the same list
  // MappingForm picks from — so names are resolved from every active shop
  // category's features, not the old QA features table.
  const fetchFeatures = async () => {
    try {
      const cats = await categoriesApi.fetchAllActiveCategories();
      const lists = await Promise.all(
        (cats || []).map(c =>
          featuresApi.getFeaturesByCategory(c.id)
            .then(fs => (fs || []).map(f => ({ ...f, categoryId: f.categoryId ?? c.id, categoryName: c.name })))
            .catch(() => [])
        )
      );
      setFeatures(lists.flat());
    } catch {
      setFeatures([]);
    }
  };

  // Add-ons on a mapping are shop Additional Features. Flatten all
  // subcategories' features into a flat list so ViewMappingModal can resolve
  // each saved id to its name/price.
  const fetchAddOns = async () => {
    try {
      const res = await Axios.fetch('/shop/additional-features');
      const subCategories = res.data?.data || res.data || [];
      const flat = (Array.isArray(subCategories) ? subCategories : []).flatMap(sc =>
        (sc.features || []).map(f => ({
          id: f.id,
          name: f.name,
          baseMonthlyPrice: f.featureDefaultPrice,
          subCategoryName: sc.subCategoryName,
        })),
      );
      setAddOns(flat);
    } catch {
      setAddOns([]);
    }
  };

  const fetchMappings = async () => {
    const res = await mappingsApi.getAll();
    setMappings(res.data.data || res.data);
  };

  const fetchQuestions = async () => {
    const res = await questionsApi.getAll();
    const qs  = res.data.data || res.data;
    const withOpts = await Promise.all(
      qs.map(async q => {
        try {
          const optRes = await answerOptionsApi.getByQuestion(q.id);
          return { ...q, answerOptions: optRes.data.data || optRes.data || [] };
        } catch {
          return { ...q, answerOptions: [] };
        }
      })
    );
    setQuestions(withOpts);
  };

  const fetchQuestionnaires = async () => {
    try {
      const res  = await questionnairesApi.getAll();
      const data = res.data.data || res.data;
      setQuestionnaires(data);
      if (data.length > 0 && !selectedQuestionnaireId) {
        setSelectedQuestionnaireId(data[0].id);
      }
    } catch {}
  };

  // A mapping "has" a country if any tier has at least one price cell for it.
  const mappingHasCountry = (mapping, countryId) =>
    ['low', 'mid', 'high'].some(t =>
      tierPriceCells(mapping, t).some(c => String(c.countryId) === String(countryId))
    );

  const filteredMappings = mappings.filter(m =>
    (!selectedQuestionnaireId || m.questionnaireId === selectedQuestionnaireId) &&
    (!selectedCountryId || mappingHasCountry(m, selectedCountryId))
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await mappingsApi.delete(id);
      Notify.success('Mapping deleted');
      fetchMappings();
    } catch {
      Notify.failure('Failed to delete');
    }
  };

  const handleView = m => { setSelectedMapping(m); setViewModalOpen(true); };
  // Refetch questionnaires before opening the form so the selected
  // questionnaire's meta.categoryIds (which scopes available features) is fresh
  // even if it was just edited on the Questionnaires step.
  const handleCreate = async () => { await fetchQuestionnaires(); setSelectedMapping(null); setView('create'); };
  const handleEdit = async m => { await fetchQuestionnaires(); setSelectedMapping(m); setView('edit'); };
  const handleCloseView = () => { setViewModalOpen(false); setSelectedMapping(null); };
  const handleBack = () => { setView('list'); setSelectedMapping(null); };
  const handleSaved = () => { setView('list'); setSelectedMapping(null); fetchMappings(); };

  // ── Lookup helpers
  const getQuestionText = (questionId) => {
    const q = questions.find(x => x.id === questionId);
    return q?.text || questionId;
  };

  const getAnswerText = (answerId) => {
    for (const q of questions) {
      const opt = q.answerOptions?.find(o => o.id === answerId);
      if (opt) return opt.answerText;
    }
    return '?';
  };

  const getFeatureName = (id) => features.find(f => f.id === id)?.name || `Feature ${String(id).slice(-6)}`;
  const getDurationName = (id) => formatDurationLabel(durations.find(d => d.id === id)) || `Duration ${String(id).slice(-6)}`;
  const getCountryMeta = (countryId) =>
    countries.find(x => String(x.id) === String(countryId));

  // Group a tier's flat price cells by country, ordered to match the
  // CountryContext list (default/known countries first). This keeps the
  // "All Countries" view from mixing currencies into one ambiguous list.
  const groupCellsByCountry = (cells) => {
    const byCountry = new Map();
    for (const cell of cells) {
      const key = String(cell.countryId);
      if (!byCountry.has(key)) byCountry.set(key, []);
      byCountry.get(key).push(cell);
    }
    const order = countries.map(c => String(c.id));
    return [...byCountry.entries()]
      .sort((a, b) => {
        const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
      .map(([countryId, groupCells]) => ({ countryId, cells: groupCells }));
  };

  // Feature names assigned to a tier
  const getTierFeatureNames = (mapping, tier) =>
    (readTierConfig(mapping).featuresByTier[tier] || []).map(getFeatureName);

  // Build condition pills from answerCombination, in the order the end user
  // meets those questions (orderByFlow: step in the question tree, then
  // orderIndex). A mapping may key on only some questions (e.g. industry,
  // budget, keywords), so the order can't come from walking its own answers.
  const buildConditionPills = (mapping) => {
    const combo = mapping.answerCombination || {};
    const scopedQuestions = questions.filter(q => q.questionnaireId === mapping.questionnaireId);
    const orderedIds = orderByFlow(scopedQuestions, Object.keys(combo));
    const pills = [];
    for (const questionId of orderedIds) {
      const answerIds = combo[questionId];
      if (answerIds === undefined) continue;
      const qText = getQuestionText(questionId);
      const ids   = Array.isArray(answerIds) ? answerIds : [answerIds];
      for (const aid of ids) {
        pills.push({ qText, aText: getAnswerText(aid) });
      }
    }
    return pills;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" gap={1.5}>
        <CircularProgress sx={{ color: ACCENT }} />
        <MDTypography variant="body2" color="text.secondary">Loading mappings…</MDTypography>
      </Box>
    );
  }

  if (!canRead) {
    return (
      <MDBox>
        <NoPermissionNotice message="Your role doesn't include read access to Answer → Plan Mappings. Ask an administrator to update your permissions." />
      </MDBox>
    );
  }

  const selectedQnaire = questionnaires.find(q => q.id === selectedQuestionnaireId);
  const qnaireCategoryIds = (Array.isArray(selectedQnaire?.meta?.categoryIds) ? selectedQnaire.meta.categoryIds : []).map(String);
  const scopedFeatureCount = qnaireCategoryIds.length
    ? features.filter(f => qnaireCategoryIds.includes(String(f.categoryId))).length
    : features.length;

  if (view !== 'list') {
    return (
      <MDBox>
        <MappingForm
          key={view === 'edit' ? selectedMapping?.id : 'create'}
          mapping={view === 'edit' ? selectedMapping : null}
          questionnaireId={selectedQuestionnaireId}
          categoryIds={
            Array.isArray(selectedQnaire?.meta?.categoryIds)
              ? selectedQnaire.meta.categoryIds
              : Array.isArray(selectedQnaire?.categoryIds)
              ? selectedQnaire.categoryIds
              : []
          }
          questions={questions}
          onBack={handleBack}
          onSaved={handleSaved}
        />
      </MDBox>
    );
  }

  return (
    <MDBox>
      {/* ── Header card ── */}
      <Card sx={{
        mb: 2.5, px: 3, py: 1.8,
        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
        boxShadow: '0 6px 24px rgba(102,126,234,0.4)', borderRadius: 3,
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AccountTreeOutlined sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                Answer → Plan Mappings
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, mt: 0.3 }}>
                {filteredMappings.length} mapping{filteredMappings.length !== 1 ? 's' : ''} — connect answer combinations to service plans
              </MDTypography>
            </Box>
          </Box>
          {canCreate && (
          <MDButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={questions.length === 0 || features.length === 0 || !selectedQuestionnaireId}
            sx={{
              bgcolor: 'rgba(255,255,255,0.22)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)', boxShadow: 'none',
              fontSize: 14, fontWeight: 600, px: 2.5, py: 1, textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.32)', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' },
            }}
          >
            + Create Mapping
          </MDButton>
          )}
        </Box>
      </Card>

      {/* ── Questionnaire selector ── */}
      <Card sx={{ mb: 2.5, px: 2.5, py: 1.8, border: '1.5px solid', borderColor: 'divider', borderRadius: 2.5, boxShadow: 'none' }}>
        <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Filter by Questionnaire</InputLabel>
            <Select
              value={selectedQuestionnaireId}
              onChange={e => setSelectedQuestionnaireId(e.target.value)}
              label="Filter by Questionnaire"
            >
              <MenuItem value="">
                <em>All Questionnaires ({mappings.length})</em>
              </MenuItem>
              {questionnaires.map(q => (
                <MenuItem key={q.id} value={q.id}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{q.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>
                      {mappings.filter(m => m.questionnaireId === q.id).length} mappings
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel shrink>Filter by Country</InputLabel>
            <Select
              value={selectedCountryId}
              onChange={e => setSelectedCountryId(e.target.value)}
              label="Filter by Country"
              displayEmpty
              notched
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                      All Countries
                    </Typography>
                  );
                }
                const c = countries.find(x => String(x.id) === String(value));
                if (!c) return 'All Countries';
                return (
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {c.flagEmoji && <span>{c.flagEmoji}</span>}
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                      {c.countryName}
                    </Typography>
                    {(c.currencyCode || c.currencySymbol) && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        ({c.currencySymbol || c.currencyCode})
                      </Typography>
                    )}
                  </Box>
                );
              }}
            >
              <MenuItem value="">
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>All Countries</Typography>
              </MenuItem>
              {countries.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {c.flagEmoji && <span>{c.flagEmoji}</span>}
                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                      {c.countryName}
                    </Typography>
                    {(c.currencyCode || c.currencySymbol) && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        ({c.currencySymbol || c.currencyCode})
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedQnaire && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 32 }} />
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.4 }}>
                  Page: <strong style={{ color: '#111827' }}>{selectedQnaire.page?.name}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>
                  {activeQuestions(questions.filter(q => q.questionnaireId === selectedQuestionnaireId)).length} questions &nbsp;·&nbsp; {scopedFeatureCount} features available
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* ── Mapping cards ── */}
      {filteredMappings.length === 0 ? (
        <Card sx={{ p: 7, textAlign: 'center', border: '1.5px dashed', borderColor: 'divider', boxShadow: 'none', borderRadius: 3 }}>
          <AccountTreeOutlined sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }} />
          <MDTypography variant="h6" color="text.secondary" mb={1}>
            {selectedCountryId
              ? 'No mappings with pricing for the selected country.'
              : selectedQuestionnaireId
                ? 'No mappings yet for the selected questionnaire.'
                : 'No mappings yet.'}
          </MDTypography>
          <MDTypography variant="body2" color="text.disabled" mb={2.5}>
            Create a mapping to connect answer combinations to service plans.
          </MDTypography>
          {selectedQuestionnaireId && canCreate && (
            <MDButton
              variant="gradient" color="info" startIcon={<Add />}
              onClick={handleCreate}
              disabled={questions.length === 0 || features.length === 0}
            >
              Create First Mapping
            </MDButton>
          )}
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredMappings.map((mapping, idx) => {
            const conditionPills = buildConditionPills(mapping);

            return (
              <Card
                key={mapping.id}
                sx={{
                  border: '1.5px solid', borderColor: 'divider', borderRadius: 3,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px rgba(102,126,234,0.12)', borderColor: `${ACCENT}40` },
                }}
              >
                <Box sx={{ p: 2.5 }}>
                  {/* ── Card header ── */}
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2}>
                    <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
                      {/* Serial badge */}
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                          {idx + 1}
                        </Typography>
                      </Box>

                      {/* Conditions pills */}
                      <Box display="flex" flexWrap="wrap" gap={0.7} flex={1} minWidth={0}>
                        {conditionPills.length > 0 ? (
                          conditionPills.map((pill, i) => (
                            <Box
                              key={i}
                              sx={{
                                fontSize: 12, color: '#374151',
                                background: '#f8fafc', border: '1px solid #e2e8f0',
                                padding: '4px 10px', borderRadius: 20,
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              }}
                            >
                              <span style={{ color: '#64748b' }}>
                                {pill.qText.length > 28 ? pill.qText.slice(0, 28) + '…' : pill.qText}
                              </span>
                              <span style={{ color: '#94a3b8', margin: '0 2px' }}>→</span>
                              <strong style={{ color: ACCENT, fontWeight: 600 }}>{pill.aText}</strong>
                            </Box>
                          ))
                        ) : (
                          <Typography sx={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                            No conditions — matches all answers (default mapping)
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status + Actions */}
                    <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                      <Chip
                        label={mapping.active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          height: 24, fontSize: 11, fontWeight: 700,
                          bgcolor: mapping.active ? 'rgba(22,163,74,0.1)' : 'rgba(107,114,128,0.1)',
                          color: mapping.active ? '#16a34a' : '#6b7280',
                          border: '1px solid',
                          borderColor: mapping.active ? 'rgba(22,163,74,0.3)' : 'rgba(107,114,128,0.25)',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                      <ActionBtn icon={<Visibility sx={{ fontSize: 16 }} />} title="View" color={ACCENT}         onClick={() => handleView(mapping)} />
                      <ActionBtn icon={<Edit       sx={{ fontSize: 16 }} />} title="Edit" color="#3b82f6"        onClick={() => handleEdit(mapping)} />
                      <ActionBtn icon={<Delete     sx={{ fontSize: 16 }} />} title="Delete" color="#ef4444"      onClick={() => handleDelete(mapping.id)} />
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2, borderColor: 'rgba(0,0,0,0.06)' }} />

                  {/* ── Tier grid (3 columns: Low / Mid / High) ── */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2 }}>
                    {(['low', 'mid', 'high']).map(tier => {
                      const tc          = TIER_COLOR[tier];
                      const td          = TIER_DIM[tier];
                      const featureNames = getTierFeatureNames(mapping, tier);
                      const priceCells   = tierPriceCells(mapping, tier)
                        .filter(c => !selectedCountryId || String(c.countryId) === String(selectedCountryId));
                      const isEmpty      = featureNames.length === 0 && priceCells.length === 0;

                      return (
                        <Box
                          key={tier}
                          sx={{
                            background: td,
                            border: `1.5px solid ${tc}44`,
                            borderTop: `3px solid ${tc}`,
                            borderRadius: 2,
                            p: 1.5,
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <TierBadge tier={tier} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: tc }}>
                              {featureNames.length} feature{featureNames.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>

                          {isEmpty ? (
                            <Typography sx={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                              Not configured
                            </Typography>
                          ) : priceCells.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {groupCellsByCountry(priceCells).map((group) => {
                                const cm  = getCountryMeta(group.countryId);
                                const sym = cm?.currencySymbol || cm?.currencyCode || '';
                                return (
                                  <Box key={group.countryId}>
                                    {/* Country header — only when viewing All Countries; redundant once a
                                        specific country is filtered, so it's hidden in that case. */}
                                    {!selectedCountryId && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, px: 0.3 }}>
                                        {cm?.flagEmoji && <span style={{ fontSize: 12, lineHeight: 1 }}>{cm.flagEmoji}</span>}
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {cm?.countryName || `Country ${String(group.countryId).slice(-4)}`}
                                        </Typography>
                                        {sym && (
                                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', flexShrink: 0 }}>
                                            {sym}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                      {group.cells.map((cell, i) => {
                                        const hasStrike = cell.priceBeforeDiscount && Number(cell.priceBeforeDiscount) > Number(cell.finalPrice);
                                        return (
                                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.7, bgcolor: '#fff', border: `1px solid ${tc}33`, borderRadius: 1.5, px: 1, py: 0.5 }}>
                                            <Typography sx={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {getDurationName(cell.durationId)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0 }}>
                                              {hasStrike && (
                                                <Typography sx={{ fontSize: 10, color: '#9ca3af', textDecoration: 'line-through' }}>
                                                  {sym}{cell.priceBeforeDiscount}
                                                </Typography>
                                              )}
                                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                                                {sym}{cell.finalPrice}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                              No pricing set
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* ── View modal (read-only stays a modal) ── */}
      <ViewMappingModal
        open={viewModalOpen}
        onClose={handleCloseView}
        mapping={selectedMapping}
        questions={questions}
        features={features}
        addOns={addOns}
        questionnaireId={selectedQuestionnaireId}
      />
    </MDBox>
  );
}

export default MappingsPage;
